import * as React from "react";
import { theme } from "@/theme/theme";
import { cn } from "@/lib/utils";

interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  variant: "new" | "inProgress" | "escalated" | "closed";
  className?: string;
}

export const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ children, variant, className, ...props }, ref) => {
    const baseStyles: React.CSSProperties = {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
      borderRadius: theme.radius.sm,
      fontSize: theme.typography.sizes.label,
      fontFamily: theme.typography.fontFamily,
      fontWeight: theme.typography.weights.medium,
      border: "none",
      whiteSpace: "nowrap" as const,
    };

    const variantStyles: React.CSSProperties = (() => {
      switch (variant) {
        case "new":
          return {
            backgroundColor: theme.colors.primary.blue,
            color: theme.colors.white,
          };
        case "inProgress":
          return {
            backgroundColor: theme.colors.primary.yellow,
            color: theme.colors.gray[900],
          };
        case "escalated":
          return {
            backgroundColor: theme.colors.primary.red,
            color: theme.colors.white,
          };
        case "closed":
          return {
            backgroundColor: theme.colors.primary.green,
            color: theme.colors.white,
          };
        default:
          return {
            backgroundColor: theme.colors.primary.blue,
            color: theme.colors.white,
          };
      }
    })();

    return (
      <div
        ref={ref}
        style={{ ...baseStyles, ...variantStyles }}
        className={cn(className)}
        role="status"
        {...props}
      >
        {children}
      </div>
    );
  },
);

Badge.displayName = "Badge";
